import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Bell, Loader2, Save, Search, Users, Clock, Plus, X, CheckSquare, Square, Building2, Mail, Smartphone, Moon, Wallet, Send } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { PortalNotificationSounds } from './PortalNotificationSounds';
import { ClockAlertEmailSwitches } from '../clock/ClockAlertEmailSwitches';
import { toast } from '../../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

const OperationsSettingsTab = ({ isViewer = false }) => {
  const token = localStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // ── HR Digest recipients ──────────────────────────────
  const [employees, setEmployees] = useState([]);
  const [recipientIds, setRecipientIds] = useState([]);
  const [recipientSearch, setRecipientSearch] = useState('');
  const [savingRecipients, setSavingRecipients] = useState(false);

  // ── Per-recipient alert preferences (channel + quiet hours) ──
  const [alertPrefs, setAlertPrefs] = useState({}); // emp_id -> {in_app, email, quiet_start, quiet_end}
  const [savingPrefs, setSavingPrefs] = useState(false);
  const DEFAULT_PREF = { in_app: true, email: false, quiet_start: '', quiet_end: '' };
  const getPref = (id) => ({ ...DEFAULT_PREF, ...(alertPrefs[id] || {}) });
  const updatePref = (id, patch) => setAlertPrefs(prev => ({ ...prev, [id]: { ...getPref(id), ...patch } }));

  // ── Report email address book (shared recipients) ──
  const [reportEmails, setReportEmails] = useState([]);
  const [newReportEmail, setNewReportEmail] = useState('');
  const [reportCc, setReportCc] = useState('hr@starsnbars.ae');
  const [savingReportEmails, setSavingReportEmails] = useState(false);

  // ── Attendance thresholds ─────────────────────────────
  const [defaultHours, setDefaultHours] = useState(8);
  const [deptRows, setDeptRows] = useState([]); // [{dept, hours}]
  const [newDept, setNewDept] = useState('');
  const [newHours, setNewHours] = useState('8');
  const [savingThresholds, setSavingThresholds] = useState(false);

  // ── Clock anti-spoofing (face threshold + geofence) ──
  const [faceThreshold, setFaceThreshold] = useState(0.5);
  const [geoRadius, setGeoRadius] = useState(100);
  const [geoBlock, setGeoBlock] = useState(true);
  const [faceBlock, setFaceBlock] = useState(true);
  const [requireGps, setRequireGps] = useState(true);
  const [gpsTolerance, setGpsTolerance] = useState(30);
  const [savingAntiSpoof, setSavingAntiSpoof] = useState(false);

  // ── Leave escalation (stale approvals → HR) ───────────
  const [autoEscalate, setAutoEscalate] = useState(true);
  const [staleDays, setStaleDays] = useState(7);
  const [clashThreshold, setClashThreshold] = useState(2);
  const [savingEscalation, setSavingEscalation] = useState(false);
  const [runningEscalation, setRunningEscalation] = useState(false);

  // ── EOSB → Finance monthly summary ────────────────────
  const [financeEmails, setFinanceEmails] = useState([]);
  const [newFinanceEmail, setNewFinanceEmail] = useState('');
  const [financeEnabled, setFinanceEnabled] = useState(false);
  const [financeDay, setFinanceDay] = useState(1);
  const [financeCc, setFinanceCc] = useState('hr@starsnbars.ae');
  const [financeUpdatedAt, setFinanceUpdatedAt] = useState(null);
  const [savingFinance, setSavingFinance] = useState(false);
  const [sendingFinance, setSendingFinance] = useState(false);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    Promise.all([
      axios.get(`${API}/api/employees`, { headers }),
      axios.get(`${API}/api/hr-digest/recipients`, { headers }),
      axios.get(`${API}/api/settings/attendance-thresholds`, { headers }),
      axios.get(`${API}/api/hr-digest/alert-prefs`, { headers }),
      axios.get(`${API}/api/report-email/recipients`, { headers }),
      axios.get(`${API}/api/settings/clock-antispoof`, { headers }),
      axios.get(`${API}/api/settings/leave-escalation`, { headers }),
      axios.get(`${API}/api/settings/eosb-finance`, { headers }),
    ]).then(([empRes, recRes, thrRes, prefRes, reRes, asRes, escRes, finRes]) => {
      if (!mounted) return;
      const activeEmps = (empRes.data || [])
        .filter(e => e.is_active && (e.employment_status || 'Active') === 'Active')
        .sort((a, b) => (a.employee_code || '').localeCompare(b.employee_code || ''));
      setEmployees(activeEmps);
      setRecipientIds(recRes.data?.employee_ids || []);
      setAlertPrefs(prefRes.data?.prefs || {});
      setReportEmails(reRes.data?.recipients || []);
      if (reRes.data?.auto_cc) setReportCc(reRes.data.auto_cc);
      const cur = thrRes.data?.current || {};
      setDefaultHours(cur.default_hours ?? 8);
      const rows = Object.entries(cur.by_department || {}).map(([dept, h]) => ({ dept, hours: h }));
      setDeptRows(rows);
      const as = asRes.data?.current || {};
      setFaceThreshold(as.face_match_threshold ?? 0.5);
      setGeoRadius(as.geofence_default_radius_m ?? 100);
      setGeoBlock(as.geofence_block ?? true);
      setFaceBlock(as.face_block ?? true);
      setRequireGps(as.require_gps ?? true);
      setGpsTolerance(as.gps_accuracy_tolerance_m ?? 30);
      const esc = escRes.data?.current || {};
      setAutoEscalate(esc.auto_escalate_enabled ?? true);
      setStaleDays(esc.stale_escalation_days ?? 7);
      setClashThreshold(esc.clash_threshold ?? 2);
      const fin = finRes.data?.current || {};
      setFinanceEmails(fin.finance_emails || []);
      setFinanceEnabled(!!fin.enabled);
      setFinanceDay(fin.day_of_month ?? 1);
      if (fin.auto_cc) setFinanceCc(fin.auto_cc);
      setFinanceUpdatedAt(fin.updated_at || null);
    }).catch(() => {})
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [headers]);

  const filteredEmployees = useMemo(() => {
    const q = recipientSearch.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter(e =>
      (e.name || '').toLowerCase().includes(q)
      || (e.employee_code || '').toLowerCase().includes(q)
      || (e.department || '').toLowerCase().includes(q)
      || (e.position || '').toLowerCase().includes(q)
    );
  }, [employees, recipientSearch]);

  // Group active employees by department for the bulk-select pickers.
  // Each entry tracks how many of the dept's employees are already in
  // recipientIds so we can render a tri-state badge (all / some / none).
  const deptGroups = useMemo(() => {
    const groups = {};
    employees.forEach(e => {
      const dept = (e.department || '— No Department —').trim() || '— No Department —';
      (groups[dept] ||= []).push(e.id);
    });
    const recipientSet = new Set(recipientIds);
    return Object.entries(groups)
      .map(([dept, ids]) => {
        const selected = ids.filter(id => recipientSet.has(id)).length;
        let state = 'none';
        if (selected === 0) state = 'none';
        else if (selected === ids.length) state = 'all';
        else state = 'some';
        return { dept, ids, total: ids.length, selected, state };
      })
      .sort((a, b) => a.dept.localeCompare(b.dept));
  }, [employees, recipientIds]);

  const toggleDepartment = (dept) => {
    const group = deptGroups.find(g => g.dept === dept);
    if (!group) return;
    setRecipientIds(prev => {
      const set = new Set(prev);
      // If everyone in the dept is already selected → remove them.
      // Otherwise → add everyone in the dept.
      if (group.state === 'all') {
        group.ids.forEach(id => set.delete(id));
      } else {
        group.ids.forEach(id => set.add(id));
      }
      return Array.from(set);
    });
  };

  const selectAllVisible = () => {
    setRecipientIds(prev => {
      const set = new Set(prev);
      filteredEmployees.forEach(e => set.add(e.id));
      return Array.from(set);
    });
  };

  const clearRecipients = () => {
    if (!window.confirm('Clear ALL recipients? The morning digest will fall back to HR-department employees.')) return;
    setRecipientIds([]);
  };

  const toggleRecipient = (id) => {
    setRecipientIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
  const addReportEmail = () => {
    const e = newReportEmail.trim();
    if (!EMAIL_RE.test(e)) { toast({ title: 'Enter a valid email', variant: 'destructive' }); return; }
    if (reportEmails.some(x => x.toLowerCase() === e.toLowerCase())) { setNewReportEmail(''); return; }
    setReportEmails(prev => [...prev, e]);
    setNewReportEmail('');
  };
  const removeReportEmail = (e) => setReportEmails(prev => prev.filter(x => x !== e));
  const saveReportEmails = async () => {
    setSavingReportEmails(true);
    try {
      const r = await axios.put(`${API}/api/report-email/recipients`, { recipients: reportEmails }, { headers });
      setReportEmails(r.data?.recipients || []);
      toast({ title: 'Report recipients saved', description: `${(r.data?.recipients || []).length} saved` });
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally { setSavingReportEmails(false); }
  };

  const saveRecipients = async () => {
    setSavingRecipients(true);
    try {
      await axios.put(`${API}/api/hr-digest/recipients`, { employee_ids: recipientIds }, { headers });
      toast({ title: 'Recipients saved', description: `${recipientIds.length} employee(s) will receive the morning digest push.` });
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setSavingRecipients(false);
    }
  };

  const savePrefs = async () => {
    setSavingPrefs(true);
    try {
      // Only persist prefs for currently-selected recipients
      const prefs = {};
      recipientIds.forEach(id => { prefs[id] = getPref(id); });
      await axios.put(`${API}/api/hr-digest/alert-prefs`, { prefs }, { headers });
      toast({ title: 'Alert preferences saved' });
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setSavingPrefs(false);
    }
  };

  const triggerDigestNow = async () => {
    try {
      const r = await axios.post(`${API}/api/hr-digest/run-now?slot=morning`, {}, { headers });
      const c = r.data?.counts || {};
      toast({ title: 'Digest built', description: `Missed: ${c.missed_clockouts || 0} · Late: ${c.late_arrivals || 0} · Absent: ${c.absences || 0}` });
    } catch (e) {
      toast({ title: 'Digest failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  const sendTestPush = async () => {
    // Pick the first selected recipient (or warn if none) so we have a real
    // employee_id to target. The backend can auto-resolve from the caller too
    // but only when the admin user has a matching employee record by name.
    const targetId = recipientIds[0];
    if (!targetId) {
      toast({
        title: 'Pick a recipient first',
        description: 'Select at least one employee in the list below — the test push will be sent to that recipient.',
        variant: 'destructive',
      });
      return;
    }
    try {
      const r = await axios.post(`${API}/api/hr-digest/recipients/test-push`,
        { employee_id: targetId }, { headers });
      toast({
        title: 'Test push sent',
        description: `Delivered to ${r.data?.employee_name || 'recipient'} (${r.data?.employee_code || ''}). Check their portal notifications.`,
      });
    } catch (e) {
      toast({ title: 'Test push failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  const addDeptRow = () => {
    const dept = newDept.trim().toUpperCase();
    const h = parseFloat(newHours);
    if (!dept) { toast({ title: 'Enter a department name', variant: 'destructive' }); return; }
    if (isNaN(h) || h < 0.5 || h > 24) { toast({ title: 'Hours must be 0.5–24', variant: 'destructive' }); return; }
    if (deptRows.some(r => r.dept === dept)) { toast({ title: 'Department already listed', variant: 'destructive' }); return; }
    setDeptRows(prev => [...prev, { dept, hours: h }]);
    setNewDept('');
    setNewHours('8');
  };

  const updateDeptRow = (idx, hours) => {
    setDeptRows(prev => prev.map((r, i) => i === idx ? { ...r, hours } : r));
  };
  const removeDeptRow = (idx) => setDeptRows(prev => prev.filter((_, i) => i !== idx));

  const saveThresholds = async () => {
    setSavingThresholds(true);
    try {
      const by_department = {};
      deptRows.forEach(r => {
        const h = parseFloat(r.hours);
        if (!isNaN(h) && h >= 0.5 && h <= 24) by_department[r.dept] = h;
      });
      await axios.put(`${API}/api/settings/attendance-thresholds`, {
        default_hours: parseFloat(defaultHours) || 8,
        by_department,
      }, { headers });
      toast({ title: 'Thresholds saved', description: 'Attendance Matrix will use these values on its next refresh.' });
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setSavingThresholds(false);
    }
  };

  const saveAntiSpoof = async () => {
    setSavingAntiSpoof(true);
    try {
      await axios.put(`${API}/api/settings/clock-antispoof`, {
        face_match_threshold: parseFloat(faceThreshold),
        geofence_default_radius_m: parseInt(geoRadius, 10),
        geofence_block: geoBlock,
        face_block: faceBlock,
        require_gps: requireGps,
        gps_accuracy_tolerance_m: parseInt(gpsTolerance, 10),
      }, { headers });
      toast({ title: 'Anti-spoofing settings saved', description: 'New punches will use these values immediately.' });
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setSavingAntiSpoof(false);
    }
  };

  const saveEscalation = async () => {
    setSavingEscalation(true);
    try {
      await axios.put(`${API}/api/settings/leave-escalation`, {
        auto_escalate_enabled: autoEscalate,
        stale_escalation_days: parseInt(staleDays, 10),
        clash_threshold: parseInt(clashThreshold, 10),
      }, { headers });
      toast({ title: 'Leave escalation settings saved' });
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setSavingEscalation(false);
    }
  };

  const runEscalationNow = async () => {
    setRunningEscalation(true);
    try {
      const res = await axios.post(`${API}/api/settings/leave-escalation/run-now`, {}, { headers });
      const n = res.data?.escalated ?? 0;
      toast({ title: 'Escalation sweep complete', description: n ? `${n} stale request(s) escalated to HR.` : 'No stale requests found.' });
    } catch (e) {
      toast({ title: 'Run failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setRunningEscalation(false);
    }
  };

  const addFinanceEmail = () => {
    const e = newFinanceEmail.trim();
    if (!EMAIL_RE.test(e)) { toast({ title: 'Enter a valid email', variant: 'destructive' }); return; }
    if (financeEmails.some(x => x.toLowerCase() === e.toLowerCase())) { setNewFinanceEmail(''); return; }
    setFinanceEmails(prev => [...prev, e]);
    setNewFinanceEmail('');
  };
  const removeFinanceEmail = (e) => setFinanceEmails(prev => prev.filter(x => x !== e));

  const saveFinance = async () => {
    setSavingFinance(true);
    try {
      const r = await axios.put(`${API}/api/settings/eosb-finance`, {
        finance_emails: financeEmails,
        enabled: financeEnabled,
        day_of_month: parseInt(financeDay, 10) || 1,
      }, { headers });
      const cur = r.data?.current || {};
      setFinanceEmails(cur.finance_emails || []);
      setFinanceUpdatedAt(cur.updated_at || null);
      toast({ title: 'EOSB Finance settings saved' });
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setSavingFinance(false);
    }
  };

  const sendFinanceNow = async () => {
    if (financeEmails.length === 0) { toast({ title: 'Add at least one Finance email first', variant: 'destructive' }); return; }
    setSendingFinance(true);
    try {
      const r = await axios.post(`${API}/api/settings/eosb-finance/send-now`, {}, { headers });
      const t = r.data?.totals || {};
      toast({ title: 'EOSB summary emailed', description: `Sent to ${(r.data?.to || []).join(', ')} (cc ${r.data?.cc}) · ${t.count || 0} pending, balance AED ${Math.round(t.balance || 0).toLocaleString()}` });
    } catch (e) {
      toast({ title: 'Send failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setSendingFinance(false);
    }
  };

  if (loading) {
    return (
      <Card><CardContent className="p-6 flex items-center gap-2 text-gray-500 text-sm">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading operations settings…
      </CardContent></Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="operations-settings-tab">
      <PortalNotificationSounds isViewer={isViewer} />
      {/* HR Digest Recipients */}
      <Card data-testid="hr-digest-recipients-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Bell className="h-5 w-5 text-violet-600" />
            HR Daily Digest — Push Recipients
          </CardTitle>
          <p className="text-xs text-gray-500">
            Selected employees receive a portal notification + browser push at 08:00 UAE when missed clock-outs, late arrivals, or absences are flagged.
            <span className="text-amber-700 ml-1">If empty, the digest falls back to all active employees in the HR department.</span>
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Bulk department picker — tap a chip to add/remove the whole dept */}
          {deptGroups.length > 0 && (
            <div className="rounded-lg border border-violet-100 bg-violet-50/40 p-3" data-testid="dept-picker">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-violet-800 flex items-center gap-1.5">
                  <Building2 className="h-3.5 w-3.5" /> Quick add by department
                </p>
                <div className="flex gap-1.5">
                  <button
                    type="button"
                    onClick={selectAllVisible}
                    disabled={isViewer}
                    className="text-[10px] uppercase tracking-wide text-violet-700 hover:text-violet-900 disabled:opacity-50"
                    data-testid="select-all-visible-btn"
                    title="Add every employee currently shown by the search/filter"
                  >
                    + Select all visible
                  </button>
                  <span className="text-violet-300">·</span>
                  <button
                    type="button"
                    onClick={clearRecipients}
                    disabled={isViewer || recipientIds.length === 0}
                    className="text-[10px] uppercase tracking-wide text-rose-600 hover:text-rose-800 disabled:opacity-50"
                    data-testid="clear-recipients-btn"
                  >
                    Clear all
                  </button>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {deptGroups.map(g => {
                  const cls =
                    g.state === 'all'   ? 'bg-violet-600 text-white border-violet-600 hover:bg-violet-700' :
                    g.state === 'some'  ? 'bg-violet-100 text-violet-800 border-violet-300 hover:bg-violet-200' :
                                          'bg-white text-gray-700 border-gray-300 hover:bg-violet-50';
                  return (
                    <button
                      key={g.dept}
                      type="button"
                      onClick={() => toggleDepartment(g.dept)}
                      disabled={isViewer}
                      className={`text-[11px] px-2 py-1 rounded-full border transition flex items-center gap-1 ${cls}`}
                      data-testid={`dept-chip-${g.dept.replace(/\s+/g, '-').toLowerCase()}`}
                      title={`${g.selected}/${g.total} selected — click to ${g.state === 'all' ? 'remove all' : 'add all'}`}
                    >
                      {g.state === 'all'  ? <CheckSquare className="h-3 w-3" /> :
                       g.state === 'some' ? <CheckSquare className="h-3 w-3 opacity-70" /> :
                                            <Square className="h-3 w-3" />}
                      <span>{g.dept}</span>
                      <span className={`text-[9px] font-mono ${g.state === 'all' ? 'text-violet-100' : 'text-gray-500'}`}>
                        {g.selected}/{g.total}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                placeholder="Search name, code, department…"
                value={recipientSearch}
                onChange={e => setRecipientSearch(e.target.value)}
                className="pl-9"
                data-testid="recipient-search"
              />
            </div>
            <Badge variant="outline" className="text-violet-700 border-violet-300">
              <Users className="h-3 w-3 mr-1" /> {recipientIds.length} selected
            </Badge>
            <Button
              size="sm"
              variant="outline"
              onClick={sendTestPush}
              disabled={isViewer || recipientIds.length === 0}
              data-testid="send-test-push-btn"
              className="border-sky-300 text-sky-700 hover:bg-sky-50"
              title={recipientIds.length === 0 ? 'Pick at least one recipient first' : 'Send a one-off test notification to the first selected recipient'}
            >
              Send test push
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={triggerDigestNow}
              disabled={isViewer}
              data-testid="run-digest-now-btn"
            >
              Run digest now
            </Button>
            <Button
              onClick={saveRecipients}
              disabled={savingRecipients || isViewer}
              className="bg-violet-600 hover:bg-violet-700 gap-1.5"
              data-testid="save-recipients-btn"
            >
              {savingRecipients ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save Recipients
            </Button>
          </div>
          <div className="border rounded-md max-h-72 overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[11px] uppercase text-gray-600 sticky top-0">
                <tr>
                  <th className="text-left p-2 w-10"></th>
                  <th className="text-left p-2">Employee</th>
                  <th className="text-left p-2">Code</th>
                  <th className="text-left p-2">Department</th>
                  <th className="text-left p-2">Position</th>
                </tr>
              </thead>
              <tbody>
                {filteredEmployees.length === 0 ? (
                  <tr><td colSpan="5" className="p-4 text-center text-gray-500 text-xs">No employees match this search</td></tr>
                ) : filteredEmployees.map(e => (
                  <tr key={e.id} className="border-t hover:bg-gray-50" data-testid={`recipient-row-${e.employee_code}`}>
                    <td className="p-2 text-center">
                      <input
                        type="checkbox"
                        checked={recipientIds.includes(e.id)}
                        onChange={() => toggleRecipient(e.id)}
                        disabled={isViewer}
                        data-testid={`recipient-cb-${e.employee_code}`}
                      />
                    </td>
                    <td className="p-2 font-medium">{e.name}</td>
                    <td className="p-2 font-mono text-xs">{e.employee_code}</td>
                    <td className="p-2 text-xs">{e.department || '—'}</td>
                    <td className="p-2 text-xs text-gray-600">{e.position || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Report Email — shared address book */}
      <Card data-testid="report-email-recipients-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Mail className="h-5 w-5 text-indigo-600" />
            Report Email Recipients
          </CardTitle>
          <p className="text-xs text-gray-500">
            Saved email addresses for the "Email" button on reports (invoices, staff exports, insurance CTC, etc.).
            Every report email is automatically CC'd to <b className="text-gray-700">{reportCc}</b> with replies routed there.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <Input
              value={newReportEmail}
              onChange={e => setNewReportEmail(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addReportEmail(); } }}
              placeholder="name@example.com"
              className="max-w-xs"
              disabled={isViewer}
              data-testid="report-email-input"
            />
            <Button variant="outline" onClick={addReportEmail} disabled={isViewer} className="gap-1" data-testid="report-email-add">
              <Plus className="h-4 w-4" /> Add
            </Button>
          </div>
          {reportEmails.length === 0 ? (
            <p className="text-xs text-gray-400 italic">No saved recipients yet — add at least 5 for quick selection when emailing reports.</p>
          ) : (
            <div className="flex flex-wrap gap-2" data-testid="report-email-list">
              {reportEmails.map(em => (
                <Badge key={em} variant="outline" className="gap-1 pr-1 text-sm font-normal" data-testid={`report-email-chip-${em}`}>
                  {em}
                  {!isViewer && (
                    <button onClick={() => removeReportEmail(em)} className="ml-1 rounded hover:bg-gray-200 p-0.5" data-testid={`report-email-remove-${em}`}>
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </Badge>
              ))}
            </div>
          )}
          <div className="flex justify-end">
            <Button onClick={saveReportEmails} disabled={savingReportEmails || isViewer}
              className="bg-indigo-600 hover:bg-indigo-700 gap-1.5" data-testid="save-report-emails-btn">
              {savingReportEmails ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save Recipients
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Per-recipient alert preferences */}
      <Card data-testid="alert-prefs-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Bell className="h-5 w-5 text-amber-600" />
            Alert Preferences — per recipient
          </CardTitle>
          <p className="text-xs text-gray-500">
            Control how each recipient is alerted for real-time flagged punches (face-mismatch / off-network) and the daily digest.
            <span className="ml-1"><b>In-app</b> = portal + browser push. <b>Email</b> = sent to the recipient's work email.</span>
            <span className="text-amber-700 ml-1">Quiet hours suppress both channels during the window (UAE time, supports overnight e.g. 22:00→07:00).</span>
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          {recipientIds.length === 0 ? (
            <p className="text-xs text-gray-400 italic">Select recipients above first — their alert preferences will appear here.</p>
          ) : (
            <>
              <div className="border rounded-md max-h-72 overflow-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-[11px] uppercase text-gray-600 sticky top-0">
                    <tr>
                      <th className="text-left p-2">Recipient</th>
                      <th className="text-center p-2"><span className="inline-flex items-center gap-1"><Smartphone className="h-3 w-3" /> In-app</span></th>
                      <th className="text-center p-2"><span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" /> Email</span></th>
                      <th className="text-center p-2"><span className="inline-flex items-center gap-1"><Moon className="h-3 w-3" /> Quiet from</span></th>
                      <th className="text-center p-2">Quiet to</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recipientIds.map(id => {
                      const emp = employees.find(e => e.id === id);
                      const p = getPref(id);
                      const code = emp?.employee_code || id;
                      return (
                        <tr key={id} className="border-t" data-testid={`alert-pref-row-${code}`}>
                          <td className="p-2">
                            <span className="font-medium">{emp?.name || id}</span>
                            <span className="text-xs text-gray-400 ml-1 font-mono">{emp?.employee_code}</span>
                          </td>
                          <td className="p-2 text-center">
                            <input type="checkbox" checked={p.in_app} disabled={isViewer}
                              onChange={e => updatePref(id, { in_app: e.target.checked })}
                              data-testid={`alert-pref-inapp-${code}`} />
                          </td>
                          <td className="p-2 text-center">
                            <input type="checkbox" checked={p.email} disabled={isViewer}
                              onChange={e => updatePref(id, { email: e.target.checked })}
                              data-testid={`alert-pref-email-${code}`} />
                          </td>
                          <td className="p-2 text-center">
                            <Input type="time" value={p.quiet_start || ''} disabled={isViewer}
                              onChange={e => updatePref(id, { quiet_start: e.target.value })}
                              className="w-28 mx-auto h-8" data-testid={`alert-pref-quiet-start-${code}`} />
                          </td>
                          <td className="p-2 text-center">
                            <Input type="time" value={p.quiet_end || ''} disabled={isViewer}
                              onChange={e => updatePref(id, { quiet_end: e.target.value })}
                              className="w-28 mx-auto h-8" data-testid={`alert-pref-quiet-end-${code}`} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end">
                <Button onClick={savePrefs} disabled={savingPrefs || isViewer}
                  className="bg-amber-600 hover:bg-amber-700 gap-1.5" data-testid="save-alert-prefs-btn">
                  {savingPrefs ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Save Preferences
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Attendance Thresholds */}
      <Card data-testid="attendance-thresholds-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Clock className="h-5 w-5 text-sky-600" />
            Attendance — Presence Thresholds (hours/day)
          </CardTitle>
          <p className="text-xs text-gray-500">
            Auto-sync marks <span className="font-mono">P</span> on the Attendance Matrix only when a staff member clocks the configured hours that day. Default applies to any department without an explicit override.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label className="text-xs">Default (hrs)</Label>
              <Input
                type="number" min="0.5" max="24" step="0.5"
                value={defaultHours}
                onChange={e => setDefaultHours(e.target.value)}
                disabled={isViewer}
                className="w-28 font-mono"
                data-testid="default-threshold-input"
              />
            </div>
            <div>
              <Label className="text-xs">Add department override</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="e.g. KITCHEN"
                  value={newDept}
                  onChange={e => setNewDept(e.target.value)}
                  disabled={isViewer}
                  className="w-48"
                  data-testid="new-dept-input"
                />
                <Input
                  type="number" min="0.5" max="24" step="0.5"
                  value={newHours}
                  onChange={e => setNewHours(e.target.value)}
                  disabled={isViewer}
                  className="w-24 font-mono"
                  data-testid="new-dept-hours-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={addDeptRow}
                  disabled={isViewer}
                  data-testid="add-dept-threshold-btn"
                >
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add
                </Button>
              </div>
            </div>
            <Button
              onClick={saveThresholds}
              disabled={savingThresholds || isViewer}
              className="bg-sky-600 hover:bg-sky-700 gap-1.5 ml-auto"
              data-testid="save-thresholds-btn"
            >
              {savingThresholds ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save Thresholds
            </Button>
          </div>
          {deptRows.length === 0 ? (
            <p className="text-xs text-gray-400 italic">No department-specific overrides. All departments use the default ({defaultHours} hrs).</p>
          ) : (
            <div className="border rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-[11px] uppercase text-gray-600">
                  <tr>
                    <th className="text-left p-2">Department</th>
                    <th className="text-right p-2 w-32">Hours/Day</th>
                    <th className="text-right p-2 w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {deptRows.map((r, i) => (
                    <tr key={r.dept} className="border-t" data-testid={`dept-threshold-row-${r.dept}`}>
                      <td className="p-2 font-medium">{r.dept}</td>
                      <td className="p-2 text-right">
                        <Input
                          type="number" min="0.5" max="24" step="0.5"
                          value={r.hours}
                          onChange={e => updateDeptRow(i, e.target.value)}
                          disabled={isViewer}
                          className="w-24 font-mono inline-block text-right"
                        />
                      </td>
                      <td className="p-2 text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-rose-600 h-7 w-7 p-0"
                          onClick={() => removeDeptRow(i)}
                          disabled={isViewer}
                          data-testid={`remove-dept-threshold-${r.dept}`}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Clock Anti-Spoofing */}
      <Card data-testid="clock-antispoof-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Clock className="h-5 w-5 text-violet-600" />
            Clock In/Out — Face & Location Sensitivity
          </CardTitle>
          <p className="text-xs text-gray-500">
            Tune how strict the kiosk is. Lower the <b>face threshold</b> to reject impostors more aggressively (stricter);
            raise it if genuine staff are wrongly blocked after appearance changes (shaving, glasses). With enforcement on,
            punches are <b>hard-blocked</b> when outside the geofence, when location is unavailable, or when the face doesn't match.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-6">
            <div>
              <Label className="text-xs">Face match threshold</Label>
              <Input
                type="number" min="0.3" max="1.0" step="0.01"
                value={faceThreshold}
                onChange={e => setFaceThreshold(e.target.value)}
                disabled={isViewer}
                className="w-28 font-mono"
                data-testid="face-threshold-input"
              />
              <p className="text-[10px] text-gray-400 mt-1 max-w-[180px]">Lower = stricter (rejects impostors). Default 0.50 (≈50% match cut-off); library default 0.60.</p>
            </div>
            <div>
              <Label className="text-xs">Default geofence radius (m)</Label>
              <Input
                type="number" min="20" max="5000" step="10"
                value={geoRadius}
                onChange={e => setGeoRadius(e.target.value)}
                disabled={isViewer}
                className="w-32 font-mono"
                data-testid="geo-radius-input"
              />
              <p className="text-[10px] text-gray-400 mt-1 max-w-[180px]">Applied to new stations / stations without an explicit radius. Default 100m.</p>
            </div>
            <div>
              <Label className="text-xs">GPS accuracy tolerance (m)</Label>
              <Input
                type="number" min="0" max="2000" step="10"
                value={gpsTolerance}
                onChange={e => setGpsTolerance(e.target.value)}
                disabled={isViewer}
                className="w-32 font-mono"
                data-testid="gps-tolerance-input"
              />
              <p className="text-[10px] text-gray-400 mt-1 max-w-[180px]">A poor GPS fix (±metres) widens the allowed radius by up to this much. Default 30m.</p>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={geoBlock} disabled={isViewer}
              onChange={e => setGeoBlock(e.target.checked)} data-testid="geo-block-toggle" />
            <span>Hard-block punches outside the geofence radius (<b>recommended on</b>)</span>
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={requireGps} disabled={isViewer}
              onChange={e => setRequireGps(e.target.checked)} data-testid="require-gps-toggle" />
            <span>Require location — block clock-in when GPS is off/denied (prevents geofence bypass; <b>recommended on</b>)</span>
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={faceBlock} disabled={isViewer}
              onChange={e => setFaceBlock(e.target.checked)} data-testid="face-block-toggle" />
            <span>Hard-block when the selfie doesn't match the employee's profile photo (<b>recommended on</b>)</span>
          </label>
          <ClockAlertEmailSwitches isViewer={isViewer} />
          <div className="flex justify-end">
            <Button onClick={saveAntiSpoof} disabled={savingAntiSpoof || isViewer}
              className="bg-violet-600 hover:bg-violet-700 gap-1.5" data-testid="save-antispoof-btn">
              {savingAntiSpoof ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save Anti-Spoofing Settings
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Leave escalation — auto-forward stale pending approvals to HR */}
      <Card data-testid="leave-escalation-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Clock className="h-4 w-4 text-amber-600" />
            Leave Approvals — Auto-escalate to HR
          </CardTitle>
          <p className="text-xs text-gray-500 mt-1">
            Leave requests left pending by a manager beyond the threshold are automatically
            forwarded to HR (the request stays pending; HR decides) and surfaced in the HR Daily Digest.
            The weekly reminder job runs this sweep every Monday.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={autoEscalate} disabled={isViewer}
              onChange={e => setAutoEscalate(e.target.checked)} data-testid="auto-escalate-toggle" />
            <span>Auto-escalate stale pending leave approvals to HR</span>
          </label>
          <div className="max-w-xs">
            <Label className="text-xs text-gray-600">Stale threshold (days pending)</Label>
            <Input type="number" min="1" max="90" value={staleDays}
              disabled={isViewer || !autoEscalate}
              onChange={e => setStaleDays(e.target.value)}
              className="mt-1" data-testid="stale-days-input" />
            <p className="text-[11px] text-gray-400 mt-1">A request awaiting a decision for this many days is treated as stale.</p>
          </div>
          <div className="max-w-xs">
            <Label className="text-xs text-gray-600">Leave clash threshold (people off at once)</Label>
            <Input type="number" min="1" max="50" value={clashThreshold}
              disabled={isViewer}
              onChange={e => setClashThreshold(e.target.value)}
              className="mt-1" data-testid="clash-threshold-input" />
            <p className="text-[11px] text-gray-400 mt-1">When this many teammates in a department are already off on the same dates, the portal leave form shows a "too many off" warning.</p>
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="outline" onClick={runEscalationNow} disabled={runningEscalation || isViewer}
              className="gap-1.5" data-testid="run-escalation-btn">
              {runningEscalation ? <Loader2 className="h-4 w-4 animate-spin" /> : <Clock className="h-4 w-4" />}
              Run sweep now
            </Button>
            <Button onClick={saveEscalation} disabled={savingEscalation || isViewer}
              className="bg-violet-600 hover:bg-violet-700 gap-1.5" data-testid="save-escalation-btn">
              {savingEscalation ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save Escalation Settings
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* EOSB → Finance monthly summary */}
      <Card data-testid="eosb-finance-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Wallet className="h-5 w-5 text-teal-600" />
            EOSB Summary → Finance
          </CardTitle>
          <p className="text-xs text-gray-500 mt-1">
            Email the <b>pending EOSB settlement summary</b> (per-employee PDF + outstanding totals) to your Finance team.
            Send it on demand, or schedule it to go out automatically once a month.
            Every email is auto-CC'd to <b className="text-gray-700">{financeCc}</b>.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-xs text-gray-600">Finance email address(es)</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              <Input
                type="email"
                value={newFinanceEmail}
                onChange={e => setNewFinanceEmail(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addFinanceEmail(); } }}
                disabled={isViewer}
                placeholder="finance@company.com"
                className="max-w-xs"
                data-testid="finance-email-input"
              />
              <Button variant="outline" onClick={addFinanceEmail} disabled={isViewer} className="gap-1" data-testid="finance-email-add">
                <Plus className="h-4 w-4" /> Add
              </Button>
            </div>
            {financeEmails.length === 0 ? (
              <p className="text-[11px] text-gray-400 mt-2 italic">No Finance recipients yet — add one or more. This is where the EOSB summary is sent.</p>
            ) : (
              <div className="flex flex-wrap gap-2 mt-2" data-testid="finance-email-list">
                {financeEmails.map(em => (
                  <Badge key={em} variant="outline" className="gap-1 pr-1 text-sm font-normal" data-testid={`finance-email-chip-${em}`}>
                    {em}
                    {!isViewer && (
                      <button onClick={() => removeFinanceEmail(em)} className="ml-1 rounded hover:bg-gray-200 p-0.5" data-testid={`finance-email-remove-${em}`}>
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </Badge>
                ))}
              </div>
            )}
          </div>

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={financeEnabled} disabled={isViewer}
              onChange={e => setFinanceEnabled(e.target.checked)} data-testid="finance-enabled-toggle" />
            <span>Auto-send the EOSB summary to Finance every month</span>
          </label>

          <div className="max-w-xs">
            <Label className="text-xs text-gray-600">Send on day of month</Label>
            <Input type="number" min="1" max="28" value={financeDay}
              disabled={isViewer || !financeEnabled}
              onChange={e => setFinanceDay(e.target.value)}
              className="mt-1" data-testid="finance-day-input" />
            <p className="text-[11px] text-gray-400 mt-1">1–28. The summary is emailed at ~06:00 UAE on this day.</p>
          </div>

          {financeUpdatedAt && (
            <p className="text-[11px] text-gray-400">Last updated: {new Date(financeUpdatedAt).toLocaleString('en-GB')}</p>
          )}

          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="outline" onClick={sendFinanceNow} disabled={sendingFinance || isViewer || financeEmails.length === 0}
              className="gap-1.5 border-teal-300 text-teal-700 hover:bg-teal-50" data-testid="finance-send-now-btn">
              {sendingFinance ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Send to Finance now
            </Button>
            <Button onClick={saveFinance} disabled={savingFinance || isViewer}
              className="bg-teal-600 hover:bg-teal-700 gap-1.5" data-testid="save-finance-btn">
              {savingFinance ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save Settings
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OperationsSettingsTab;
